/**
 * 단순 stacked bar 시계열 — SVG 직접 그림 (chart lib 의존성 제거).
 * 일별 x 소스별 비용을 누적 막대로 표시.
 */
import { useMemo } from 'react';
import { sourceColor, sourceLabel, formatKrw } from '../lib/format';

interface Point {
  day: string; // YYYY-MM-DD
  source: string;
  cost: number;
}

interface Props {
  points: Point[];
  /** 표시할 마지막 N일. 데이터에 없는 날도 0으로 채움. */
  days?: number;
  height?: number;
}

export function StackedBarChart({ points, days = 30, height = 200 }: Props) {
  const { rows, sources, maxDayTotal } = useMemo(() => {
    // 마지막 N일 구성 (오늘 포함)
    const today = new Date();
    const dayList: string[] = [];
    for (let i = days - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      dayList.push(d.toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' }));
    }
    const byDay = new Map<string, Map<string, number>>();
    for (const d of dayList) byDay.set(d, new Map());
    const sourceSet = new Set<string>();
    for (const p of points) {
      sourceSet.add(p.source);
      const dayMap = byDay.get(p.day);
      if (!dayMap) continue;
      dayMap.set(p.source, (dayMap.get(p.source) ?? 0) + p.cost);
    }
    const sortedSources = Array.from(sourceSet).sort();
    const rows = dayList.map((day) => {
      const m = byDay.get(day) ?? new Map<string, number>();
      const segs = sortedSources.map((s) => ({ source: s, cost: m.get(s) ?? 0 }));
      const total = segs.reduce((a, b) => a + b.cost, 0);
      return { day, segs, total };
    });
    const maxDayTotal = rows.reduce((a, r) => Math.max(a, r.total), 0);
    return { rows, sources: sortedSources, maxDayTotal };
  }, [points, days]);

  const barWidth = 18;
  const gap = 4;
  const padLeft = 56;
  const padBottom = 28;
  const padTop = 8;
  const innerHeight = height - padBottom - padTop;
  const width = padLeft + rows.length * (barWidth + gap) + 8;

  // y축 tick
  const yMax = maxDayTotal > 0 ? niceMax(maxDayTotal) : 100;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((p) => p * yMax);

  return (
    <div className="space-y-3">
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
        {/* grid + y labels */}
        {yTicks.map((t) => {
          const y = padTop + innerHeight - (t / yMax) * innerHeight;
          return (
            <g key={t}>
              <line
                x1={padLeft}
                x2={width - 4}
                y1={y}
                y2={y}
                stroke="var(--border)"
                strokeDasharray="2 2"
                strokeWidth={1}
              />
              <text x={padLeft - 6} y={y + 4} textAnchor="end" fontSize={10} fill="var(--text-muted)">
                {formatShort(t)}
              </text>
            </g>
          );
        })}
        {/* bars */}
        {rows.map((row, i) => {
          let yCursor = padTop + innerHeight;
          const x = padLeft + i * (barWidth + gap);
          return (
            <g key={row.day}>
              {row.segs.map((seg) => {
                if (seg.cost <= 0) return null;
                const h = (seg.cost / yMax) * innerHeight;
                yCursor -= h;
                return (
                  <rect
                    key={seg.source}
                    x={x}
                    y={yCursor}
                    width={barWidth}
                    height={Math.max(1, h)}
                    fill={sourceColor(seg.source)}
                  >
                    <title>{`${row.day} ${sourceLabel(seg.source)}: ${formatKrw(seg.cost)}`}</title>
                  </rect>
                );
              })}
              {/* x label — 5일마다만 표시 */}
              {i % 5 === 0 || i === rows.length - 1 ? (
                <text
                  x={x + barWidth / 2}
                  y={height - 8}
                  textAnchor="middle"
                  fontSize={10}
                  fill="var(--text-muted)"
                >
                  {row.day.slice(5)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      <div className="flex gap-4 text-xs text-text-secondary">
        {sources.map((s) => (
          <div key={s} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ background: sourceColor(s) }} />
            <span>{sourceLabel(s)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function niceMax(v: number): number {
  if (v <= 0) return 100;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / pow;
  if (norm <= 1) return 1 * pow;
  if (norm <= 2) return 2 * pow;
  if (norm <= 5) return 5 * pow;
  return 10 * pow;
}

function formatShort(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M₩`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K₩`;
  if (n >= 100) return `${Math.round(n)}₩`;
  return `${n.toFixed(0)}₩`;
}
